import { useState, useMemo } from "react";
import { CheckboxTreeView } from "../CheckboxTreeView";
import { ModalContent } from "./styles";
import { X } from "../ui/icons";
import { TextField, InputAdornment } from "@mui/material";
import { mockLayers, Layer } from "../../global/constants/layers";
import LayersFilter from "../LayersFilter";
import { useLayerPermissions } from "../../hooks/useLayerPermissions";
import { useLayers } from "../../context/LayersContext";
import { useImportedLayers } from "../../context/ImportedLayersContext";
import { useDgcChart } from "../../context/DgcChartContext";
import { CONJ_DGC_LAYER_NAME } from "../../global/constants/dgcConjuntos";
import IconButton from "../ui/buttons/icon-button";
import Typography from "../Typography";

const LayersModal = () => {
  const { canViewLayer } = useLayerPermissions();
  const { setLayerVoltageFilter } = useLayers();
  const { openEstado } = useDgcChart();
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const toggleSearch = () => {
    setShowSearch(!showSearch);
    setSearchTerm("");
  };

  const handleCloseSearch = () => {
    setShowSearch(false);
    setSearchTerm("");
  };

  // Group layers by category and filter by permissions and search
  const groupedLayers = useMemo(() => {
    // Filter layers by permissions first, and exclude permanent layers and Projeções category
    const allowedLayers = mockLayers.filter(
      (layer) =>
        canViewLayer(layer.layerName) &&
        !layer.permanent &&
        layer.category !== "Projeções"
    );

    // Collect titles that appear as subLayers of another layer — these won't
    // show up as standalone tree items (they're rendered inline by the parent)
    const subLayerTitles = new Set(
      allowedLayers.flatMap((l) => l.subLayers?.map((s) => s.layerTitle) ?? [])
    );

    // Filter by search term if provided, excluding sub-layers from top-level
    const searchFiltered = (
      searchTerm
        ? allowedLayers.filter((layer) =>
            layer.title.toLowerCase().includes(searchTerm.toLowerCase())
          )
        : allowedLayers
    ).filter((layer) => !subLayerTitles.has(layer.title));

    // Group by category
    const grouped = searchFiltered.reduce((acc, layer) => {
      if (!acc[layer.category]) {
        acc[layer.category] = [];
      }
      acc[layer.category].push(layer);
      return acc;
    }, {} as Record<string, Layer[]>);

    const toTreeLeaf = (layer: Layer) => ({
      title: layer.title,
      layerName: layer.layerName,
      voltageOptions: layer.voltageOptions,
      voltageGroupLabel: layer.voltageGroupLabel,
      voltageSubLayers: layer.voltageSubLayers,
      subLayers: layer.subLayers,
      subLayerGroupLabel: layer.subLayerGroupLabel,
      info: layer.info,
      chartButtonTooltip: layer.chartButtonTooltip,
    });

    // Convert to tree structure for CheckboxTreeView. Layers that declare a
    // `subcategory` are nested one level deeper, so a category can hold groups
    // (e.g. "Mapa Solar e Eólico" → "Solar" / "Eólico"). Layers without a
    // subcategory stay directly under the category, above the groups.
    return Object.entries(grouped).map(([category, layers]) => {
      const ungrouped = layers.filter((layer) => !layer.subcategory);

      const subGroups = layers.reduce((acc, layer) => {
        if (!layer.subcategory) return acc;
        if (!acc[layer.subcategory]) {
          acc[layer.subcategory] = [];
        }
        acc[layer.subcategory].push(layer);
        return acc;
      }, {} as Record<string, Layer[]>);

      return {
        title: category,
        children: [
          ...ungrouped.map(toTreeLeaf),
          ...Object.entries(subGroups).map(([subcategory, subLayers]) => ({
            title: subcategory,
            children: subLayers.map(toTreeLeaf),
          })),
        ],
      };
    });
  }, [searchTerm, canViewLayer]);

  return (
    <div>
      <ModalContent>
        <>
          {showSearch && (
            <TextField
              fullWidth
              placeholder="Digite o título da camada..."
              style={{ marginBottom: "10px" }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCloseSearch}>
                      <X size={20} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
          <Typography variant="subtitle1" color="#080D1A" className="py-2">
            Camadas
          </Typography>
          <CheckboxTreeView
            data={groupedLayers}
            onVoltageFilterChange={(layerName, values) =>
              setLayerVoltageFilter(layerName, values)
            }
            onChartClick={(layerName) => {
              if (layerName === CONJ_DGC_LAYER_NAME) openEstado();
            }}
          />

          {/* Search and clear selected layers */}
          <LayersFilter toggleSearch={toggleSearch} />
        </>
      </ModalContent>
    </div>
  );
};

export default LayersModal;
